<template>
  <div class="product-list">
    <div class="container">
      <div class="row">
        <!-- Sidebar -->
        <div v-if="showSidebar" class="d-none d-lg-flex col-lg-3 col-xl-2">
          <category-overview :category="topLevelCategory" />
        </div>

        <!-- Main Content -->
        <div class="col-12" :class="{ 'col-lg-9 col-xl-10': showSidebar }">
          <!-- Top Bar -->
          <div class="row mb-6">
            <!-- Filter -->
            <div class="col-12">
              <product-filter
                v-if="facets"
                class="w-100 -desktop d-none d-lg-block"
                :facets="selectedFilters && selectedFilters.length ? productList.facets : facets"
                :hide-custom-filters="hideCustomFilters"
                @filterChanged="filterChanged"
              />
            </div>

            <!-- controls: desktop -->
            <div class="product-list__controls mt-8 w-100 d-none d-lg-flex">
              <!-- View Switcher -->
              <div class="d-flex col-6 col-lg-4 align-items-center">
                <div class="product-list__sort" @click="changeCurrentView()">
                  <base-icon icon="list" class="product-list__sort-icon text-primary mr-4" />
                  <small
                    class="font-weight-bold"
                    v-html="currentView === 'row' ? $t('ShowGridView', 'Vis som grid') : $t('ShowListView', 'Vis som liste')"
                  />
                </div>
              </div>
              <!-- Total items -->
              <div class="d-flex col-lg-4 align-items-center justify-content-center font-weight-bold">
                <small v-if="productList.totalItems" class="font-weight-bold"
                  >{{ $t('ProductsTotal', 'Antal produkter') }} {{ productList.totalItems }}</small
                >
              </div>
              <!-- Sorting -->
              <div v-if="!hideSorting && selectedSorting" class="d-flex col-lg-4 align-items-center justify-content-end w-25">
                <small class="mr-3 font-weight-bold">{{ $t('Sorting', 'Sortering') }}</small>
                <base-select v-model="selectedSorting" :options="sortingOptions" class="-secondary" />
              </div>
            </div>

            <!-- controls: mobile -->
            <div class="product-list__controls w-100 d-flex d-lg-none">
              <div v-if="showSidebar" class="col">
                <transition name="fade">
                  <base-button v-if="topLevelCategory" class="-outline-primary" @click="sidebarPanelIsOpen = true">
                    <span class="font-weight-bold">{{ `Åben ${topLevelCategory?.link.name}` }}</span>
                  </base-button>
                </transition>
                <sidebar :visible="sidebarPanelIsOpen" @close="sidebarPanelIsOpen = false">
                  <category-overview :category="topLevelCategory" />
                </sidebar>
              </div>
              <!-- Filtering -->
              <div class="col d-flex justify-content-end">
                <div v-if="!hideFiltering">
                  <base-button class="product-list__sort -primary" @click="filterPanelIsOpen = true">
                    <span class="font-weight-bold">{{ $t('Filter', 'Filter') }}</span>
                  </base-button>
                  <sidebar :visible="filterPanelIsOpen" title="Filter" @close="filterPanelIsOpen = false">
                    <div v-if="!hideSorting && selectedSorting" class="text-left text-lg-right product-list__sort-select">
                      <small class="mr-lg-3 font-weight-bold">{{ $t('Sorting', 'Sortering') }}</small>
                      <base-select v-model="selectedSorting" :options="sortingOptions" class="-secondary" />
                    </div>
                    <product-filter
                      v-if="facets"
                      class="w-100 -mobile"
                      :facets="selectedFilters && selectedFilters.length ? productList.facets : facets"
                      :hide-custom-filters="hideCustomFilters"
                      @filterChanged="filterChanged"
                    />
                    <template v-slot:content-bottom>
                      <div class="px-4 px-md-7 py-5">
                        <base-button class="a-button -small -primary w-100 mb-4" @click="filterPanelIsOpen = false">
                          {{ $t('SeeAll', 'Se alle') }} ({{ productList.totalItems }}) {{ $t('Results', 'resultater') }}
                        </base-button>
                      </div>
                    </template>
                  </sidebar>
                </div>
              </div>
            </div>
          </div>

          <!-- Product List -->
          <div class="row">
            <div
              v-for="product in productList.items"
              :key="product.sku"
              :class="currentView === 'card' ? 'col-12 col-sm-6 col-lg-4 col-xl-3 mb-6' : 'col-12'"
            >
              <product-card-horizontal
                v-if="currentView === 'row'"
                :product="product"
                :show-add-to-cart-button="true"
                :is-recommended="isRecommended"
                :is-search-result="isSearchResult"
                @click.native="setScrollPosition"
              />
              <product-card
                v-else
                :product="product"
                :show-add-to-cart-button="true"
                :is-recommended="isRecommended"
                :is-search-result="isSearchResult"
                @click.native="setScrollPosition"
              />
            </div>
          </div>
          <div v-if="isPageLoad" class="row">
            <div
              v-for="(n, i) in itemsPerPage * (currentPage - loadedPages)"
              :key="i"
              :class="currentView === 'card' ? 'col-12 col-sm-6 col-lg-4 col-xl-3 mb-6' : 'col-12'"
            >
              <skeleton-loader v-if="currentView === 'row'" class="m-3" layout="row" :aspect-ratio="1142 / 162" />
              <skeleton-loader v-else class="m-3" layout="card" :aspect-ratio="276 / 462" />
            </div>
          </div>

          <div v-if="!hidePagination && productList.totalItems" class="row">
            <div class="col-12 text-center my-5">
              {{ $t('Showing', 'Viser') }}
              <span> {{ totalVisible }} </span>
              {{ $t('Of', 'af') }} {{ productList.totalItems }}
            </div>
            <div class="col-12 d-flex justify-content-center">
              <load-more
                :page="currentPage + 1"
                :loading="isLoading"
                :text="$t('loadMoreResults', 'Indlæs flere')"
                :disabled="!productList.items.length || productList.totalItems <= productList.items.length"
                @load-more="onLoadMore"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonLoader from '@/components/atoms/SkeletonLoader'
import Sidebar from '@/components/organisms/Sidebar.vue'
import BaseButton from '@/components/atoms/BaseButton'
import BaseIcon from '@/components/atoms/BaseIcon'
import ProductCard from '@/components/organisms/ShopCommerce/ProductCard.vue'
import ProductCardHorizontal from '@/components/organisms/ShopCommerce/ProductCardHorizontal.vue'
import ProductFilter from '@/components/molecules/ShopCommerce/ProductFilter.vue'
import CategoryOverview from '@/components/molecules/ShopCommerce/CategoryOverview.vue'
import LoadMore from '@/components/molecules/ShopCommerce/LoadMore.vue'
import BaseSelect from '@/components/atoms/BaseSelect'
import { trackProductImpressions } from '@/utils/enhancedEcomTracking'

import { updateQueryStringParam } from '@/utils/query'

export default {
  name: 'ProductList',
  components: {
    ProductCard,
    BaseIcon,
    BaseButton,
    ProductCardHorizontal,
    ProductFilter,
    BaseSelect,
    Sidebar,
    SkeletonLoader,
    CategoryOverview,
    LoadMore
  },
  props: {
    productList: {
      type: Object,
      default: () => {}
    },
    facetsNames: {
      type: Object,
      default: () => {}
    },
    facets: {
      type: Array,
      default: () => null
    },
    itemsPerPage: {
      type: Number,
      default: 12
    },
    currentPage: {
      type: Number,
      default: 1
    },
    loadedPages: {
      type: Number,
      default: 0
    },
    isPageLoad: {
      type: Boolean,
      default: true
    },
    isFavoriteList: {
      type: Boolean,
      default: false
    },
    hideSorting: {
      type: Boolean,
      default: false
    },
    hideFiltering: {
      type: Boolean,
      default: false
    },
    hideCustomFilters: {
      type: Boolean,
      default: false
    },
    hidePagination: {
      type: Boolean,
      default: false
    },
    isRecommended: {
      type: Boolean,
      default: false
    },
    isSearchResult: {
      type: Boolean,
      default: false
    },
    category: {
      type: Object,
      default: () => {}
    },
    showSidebar: {
      type: Boolean,
      default: false
    },
    defaultSorting: {
      type: String,
      default: 'Relevans'
    }
  },
  data() {
    return {
      selectedFilters: [],
      currentView: 'card',
      filterPanelIsOpen: false,
      sidebarPanelIsOpen: false,
      topLevelCategory: null,
      selectedSorting: null,
      initialLoad: true,
      sortingOptions: [
        {
          name: 'Relevans',
          value: JSON.stringify({
            orderBy: '',
            order: ''
          })
        },
        {
          name: 'Nyheder',
          value: JSON.stringify({
            orderBy: 'age',
            order: 'asc'
          })
        },
        {
          name: 'Laveste pris',
          value: JSON.stringify({
            orderBy: 'price',
            order: 'asc'
          })
        },
        {
          name: 'Højeste pris',
          value: JSON.stringify({
            orderBy: 'price',
            order: 'desc'
          })
        }
      ]
    }
  },
  computed: {
    isLoading() {
      return this.$store.getters['products/productList/isLoading']
    },
    totalVisible() {
      let total =
        this.currentPage * this.itemsPerPage <= this.productList.totalItems
          ? this.currentPage * this.itemsPerPage
          : this.productList.totalItems
      return total || this.itemsPerPage
    },
    shopNavigation() {
      return this.$store.getters['navigation/getShopNavigation']
    }
  },
  watch: {
    category: {
      handler() {
        this.getTopLevelCategory()
      },
      deep: true
    },
    selectedSorting(to, from) {
      if (from === null) {
        return
      }
      this.$emit('sorting-changed', { filters: this.selectedFilters, sorting: JSON.parse(this.selectedSorting), isReload: true })
    },
    currentView() {
      this.$cookies.set('productlist-layout', this.currentView, {
        path: '/',
        maxAge: 60 * 60 * 24 * 7
      })
    }
  },
  async created() {
    if (this.$cookies.get('productlist-layout')) {
      this.currentView = this.$cookies.get('productlist-layout')
    }
  },
  mounted() {
    trackProductImpressions(this.productList?.items, this.$router.currentRoute.path.split('/').slice(-1)[0], this.$cookies.get('currency'))

    this.selectedSorting = this.sortingOptions.find((option) => option.name === this.defaultSorting).value

    this.$emit('list-mounted', { filters: [], sorting: JSON.parse(this.selectedSorting), isReload: true })

    if (this.category) {
      this.getTopLevelCategory()
    }
  },
  methods: {
    onLoadMore() {
      this.$emit('clicked-load-more', { filters: this.selectedFilters, sorting: JSON.parse(this.selectedSorting), isReload: false })
    },
    changeCurrentView() {
      this.currentView = this.currentView === 'row' ? 'card' : 'row'
    },
    filterChanged(selectedFilters) {
      this.selectedFilters = selectedFilters

      this.$emit('filter-changed', { filters: this.selectedFilters, sorting: JSON.parse(this.selectedSorting), isReload: true })
    },
    setScrollPosition() {
      if (process.client) {
        updateQueryStringParam('position', parseInt(window.top.scrollY))
      }
    },

    // Category navigation Sidebar
    findTopLevelNode(currentNodeId, nodes) {
      for (let node of nodes) {
        if (node.children && node.children.length > 0) {
          const found = this.findDescendant(node.children, currentNodeId)
          if (found) {
            this.topLevelCategory = node
          }
        }
        if (node.id === currentNodeId) {
          this.topLevelCategory = node
        }
      }
      return null
    },
    findDescendant(children, currentNodeId) {
      for (let child of children) {
        if (child.id === currentNodeId) {
          return true
        }
        if (child.children && child.children.length > 0) {
          const found = this.findDescendant(child.children, currentNodeId)
          if (found) {
            return true
          }
        }
      }
      return false
    },
    getTopLevelCategory() {
      const currentNodeId = this.category?.guid
      this.findTopLevelNode(currentNodeId, this.shopNavigation)
    }
    // END: Category navigation Sidebar
  }
}
</script>
